import { factory, getResponseCamelization } from '@repo-level/core-front';
import { buildCmsUrl, getResponseCMS } from 'api/cms/base';
import { getLocalizedPath } from '../helpers';

type FaqItem = {
  question: string;
  answer: string;
};

interface ISeoContentResponse {
  path: string;
  content: string;
  locale: string;
  faqItems: FaqItem[];
  faqTitle: string;
}

export const formatHtml = (htmlString?: string) => {
  if (!htmlString) return;

  return htmlString
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/<table(\s[^>]*)?>/gi, '<div style="overflow-x:auto"><table$1>')
    .replace(/<\/table>/gi, '</table></div>')
    .replace(/target="_blank"/gi, 'target="_blank" rel="noopener noreferrer"')
    .trim();
};

const fetchSeoContent = factory(
  (r) => (routePath: string) =>
    r<ISeoContentResponse[]>(
      'get',
      buildCmsUrl({
        path: 'seo-contents',
        additionalParams: {
          'filters[path][$eq]': routePath,
          populate: 'deep',
        },
      }),
      undefined,
      { transformResponse: [getResponseCamelization(), getResponseCMS] }
    )
);

export interface IGetSeoContentData
  extends Pick<ISeoContentResponse, 'faqItems' | 'content' | 'faqTitle'> {}

export const buildFaqSchema = (faqItems: FaqItem[]): string =>
  JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer.replace(/<[^>]*>/g, ''),
      },
    })),
  });

export const getSeoContent = async (
  locale: string,
  routePath: string
): Promise<IGetSeoContentData | undefined> => {
  const fullPath = getLocalizedPath(locale, routePath);

  try {
    const response = await fetchSeoContent(fullPath);
    const data = response.data?.[0];

    return {
      content: formatHtml(data?.content) ?? '',
      faqItems: data?.faqItems ?? [],
      faqTitle: data?.faqTitle ?? '',
    };
  } catch (error) {
    console.error('SEO content fetch error:', error);
  }
};
